<template>
  <v-card class="pa-2 mb-5">
    <v-toolbar class="mb-2">
      <v-layout align-center>
        <span class="text-h5">Active Rentals</span>
        <v-spacer />
        <v-btn
          data-testid="quick-pay-sms-btn"
          class="quick-pay-sms-btn indigo"
          outlined
          @click="handleQuickPaySMS"
        >
          <v-icon class="mr-3">send_to_mobile</v-icon>SMS
        </v-btn>
        <v-btn
          data-testid="quick-pay-email-btn"
          class="quick-pay-email-btn orange lighten-1 ml-4"
          outlined
          @click="handleQuickPayEmail"
        >
          <v-icon class="mr-3">mail_outline</v-icon>Email
        </v-btn>
        <take-payment
          v-if="ledgers && ledgers.length"
          :convenienceFeeStatus="convenienceFeeStatus"
          @success="paymentSuccess"
        />
      </v-layout>
    </v-toolbar>
    <v-flex
      v-if="!ledgers || !ledgers.length"
      class="subheading mx-3 my-4"
      data-testid="no-rentals-message"
    >
      There are no active rentals for {{ tenant.first_name }} {{ tenant.last_name }}.
    </v-flex>
    <v-layout v-else column>
      <v-expansion-panels
        data-testid="tenant-rentals-expansion-panel"
        class="tenant-rentals-expansion-panel my-2 secondary"
        v-model="selectedLedger"
        focusable
      >
        <v-expansion-panel
          :data-testid="`tenant-rentals-unit-${ledger.facility_unit.name}-expansion-panel`"
          :class="`tenant-rentals-unit-${ledger.facility_unit.name}-expansion-panel`"
          v-for="(ledger, i) in sortedLedgers"
          :key="i"
        >
        <v-expansion-panel-header>
          <h3
            title
            @click="resetData"
            data-testid="panel-header"
          >
            Unit {{ ledger.facility_unit.name }}
          </h3>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card>
            <v-card-text>
              <v-layout
                column
                class="subheading mt-1 mb-4"
                justify-space-between
              >
                <tenant-rental-details
                  v-if="activeLedger"
                  :ledger="ledger"
                  :loading="unitDetailsLoading"
                  :unitFeatures="unitFeatures"
                />
                <v-flex>
                  <v-layout column>
                    <payment-history
                      v-if="paymentHistory && paymentHistory.length"
                      :paymentHistory="paymentHistory"
                      @hideHistory="paymentHistory = []"
                    />
                    <ledger-notes
                      v-if="ledgerNotes && ledgerNotes.length"
                      :loading="notesLoading"
                      :ledgerNotes="ledgerNotes"
                      @hideNotes="ledgerNotes = []"
                    />
                    <ledger-statement
                      v-if="ledgerStatement && ledgerStatement.length"
                      :ledgerStatement="ledgerStatement"
                      @hideStatement="ledgerStatement = []"
                    />
                  </v-layout>
                </v-flex>
              </v-layout>
              <v-divider class="my-3" />
              <v-layout justify-space-between>
                <v-flex class="px-3">
                  <update-billing :ledger="ledger" @success="billingUpdate" />
                </v-flex>
                <v-flex class="px-3">
                  <v-btn
                    data-testid="payment-history-btn"
                    class="payment-history-btn teal large white--text pl-3 pr-4"
                    block
                    :loading="historyLoading"
                    :disabled="!!ledgerNotes.length || !!ledgerStatement.length"
                    @click.native="toggleComponent(ledger.pms_ledger_id, 'paymentHistory')"
                  >
                    <v-icon class="mr-2">attach_money</v-icon>
                    <span v-if="!paymentHistory || !paymentHistory.length">Get Payment History</span>
                    <span v-else>Hide Payment History</span>
                  </v-btn>
                </v-flex>
                <v-flex class="px-3">
                  <v-btn
                    data-testid="ledger-statement-btn"
                    class="ledger-statement-btn deep-orange large white--text pl-3 pr-4"
                    block
                    :loading="statementLoading"
                    :disabled="!!ledgerNotes.length || !!paymentHistory.length"
                    @click.native="toggleComponent(ledger.pms_ledger_id, 'ledgerStatement')"
                  >
                    <v-icon class="mr-2">receipt</v-icon>
                    <span v-if="!ledgerStatement || !ledgerStatement.length">Get Statement</span>
                    <span v-else>Hide Statement</span>
                  </v-btn>
                </v-flex>
                <v-flex class="px-3">
                  <v-btn
                    data-testid="ledger-notes-btn"
                    class="ledger-notes-btn primary large white--text pl-3 pr-4"
                    block
                    :loading="notesLoading"
                    :disabled="!!ledgerStatement.length || !!paymentHistory.length"
                    @click.native="toggleComponent(ledger.pms_ledger_id, 'ledgerNotes')"
                  >
                    <v-icon class="mr-2">notes</v-icon>
                    <span v-if="!ledgerNotes || !ledgerNotes.length">Get Notes</span>
                    <span v-else>Hide Notes</span>
                  </v-btn>
                </v-flex>
                <v-flex class="px-3">
                  <create-note
                    :ledgerId="ledger.pms_ledger_id"
                    :ledgers="ledgers"
                    @noteAdded="getNotes(ledger.pms_ledger_id)"
                  />
                </v-flex>
                <v-flex class="px-3">
                  <move-out :ledgerId="ledger.pms_ledger_id" :ledgers="ledgers" />
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-layout>
    <application-modal
      v-model="quickPayModal"
      @cancel="closeQuickPayModal"
      @collapse="closeQuickPayModal"
      :width="'750'"
    >
      <div slot="modal-title">
        <v-layout align-center>
          Send QuickPay {{ quickPayEmail ? 'Email' : 'SMS' }}
        </v-layout>
      </div>
      <div slot="modal-body">
        <v-layout>
          <v-flex>
            <h3 class="text-h6 ml-2 mb-4">Enter the Tenant's {{ quickPayEmail ? 'Email' : 'Phone Number'}}</h3>
            <app-input
              v-if="quickPaySMS"
              data-testid="quick-pay-sms-input"
              class="quick-pay-sms-input ml-2"
              v-model="quickPaySMSNumber"
              label="SMS #"
              type="phone"
              :rules="'required'"
              placeholder="(ex. 303-123-4567)"
              filled
            ></app-input>
            <app-input
              v-if="quickPayEmail"
              data-testid="quick-pay-email-input"
              class="quick-pay-email-input ml-2"
              v-model="quickPayEmailAddress"
              label="Email Address"
              type="email"
              :rules="'required'"
              placeholder="johndoe@email.com"
              filled
            ></app-input>
          </v-flex>
        </v-layout>
      </div>
      <v-btn
        data-testid="submit-quick-pay-btn"
        class="submit-quick-pay-btn"
        slot="create-button"
        color="primary white--text"
        :loading="quickPayLoading"
        :disabled="!invalidQuickPay"
        @click="handleSendQuickPay"
      >
        Send {{ quickPayEmail ? 'Email' : 'SMS' }}
      </v-btn>
    </application-modal>
  </v-card>
</template>

<script>
import ApplicationModal from "@/components/ApplicationModal";
import CreateNote from "@/components/tenant-rentals/actions/CreateNote";
import LedgerNotes from "@/components/tenant-rentals/actions/LedgerNotes";
import LedgerStatement from "@/components/tenant-rentals/actions/LedgerStatement";
import MoveOut from "@/components/tenant-rentals/actions/MoveOut";
import PaymentHistory from "@/components/tenant-rentals/actions/PaymentHistory";
import TakePayment from "@/components/tenant-rentals/actions/TakePayment/TakePayment";
import TenantRentalDetails from "@/components/tenant-rentals/partials/TenantRentalDetails";
import UpdateBilling from "@/components/tenant-rentals/actions/UpdateBilling";
import { mapState } from "vuex";

export default {
  name: "TenantRentals",

  components: {
    ApplicationModal,
    CreateNote,
    LedgerNotes,
    LedgerStatement,
    MoveOut,
    PaymentHistory,
    TakePayment,
    TenantRentalDetails,
    UpdateBilling
  },

  props: {
    convenienceFeeStatus: {
      type: [Object, String, null]
    }
  },

  data() {
    return {
      notesLoading: false,
      statementLoading: false,
      unitDetailsLoading: false,
      historyLoading: false,
      unitDetailsModal: false,

      unitFeatures: [],
      ledgerNotes: [],
      ledgerStatement: [],
      paymentHistory: [],

      selectedLedger: null,

      quickPayModal: false,
      quickPayEmail: false,
      quickPaySMS: false,
      quickPayLoading: false,
      quickPaySMSNumber: "",
      quickPayEmailAddress: ""
    };
  },

  methods: {
    billingUpdate() {
      this.$emit("billingUpdate");
    },

    paymentSuccess() {
      this.$emit("paymentSuccess");
    },

    getUnitDetails() {
      const unitId = this.activeLedger.facility_unit ? this.activeLedger.facility_unit.id : null;
      if (unitId) {
        let payload = {
          facilityId: this.$route.params.facilityId,
          unit_id: this.activeLedger.facility_unit.sitelink_id
        };

        this.unitDetailsLoading = true;

        this.$store.dispatch("getUnitFeatures", payload)
          .then(resp => {
            this.unitFeatures = resp.data;
            this.unitDetailsLoading = false;
            this.unitDetailsModal = true;
          })
          .catch(err => {
            this.$store.dispatch("handleError", err);
            this.unitDetailsLoading = false;
          });
      }
    },

    toggleComponent(ledgerId, type) {
      if (this[type] && this[type].length) {
        this[type] = [];
      } else if (type === "paymentHistory") {
        this.getPaymentHistory(ledgerId);
      } else if (type === "ledgerNotes") {
        this.getNotes(ledgerId);
      } else if (type === "ledgerStatement") {
        this.getStatement(ledgerId);
      };
    },

    getPaymentHistory(ledgerId) {
      this.historyLoading = true;

      let payload = {
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId,
        ledgerId: ledgerId
      };

      this.$store.dispatch("getPaymentHistory", payload)
        .then(resp => {
          this.paymentHistory = resp.data;
          this.historyLoading = false;
          if (!this.paymentHistory.length) {
            this.$store.dispatch("handleAlert", "No payment history found for this unit.");
          }
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.historyLoading = false;
        });
    },

    getNotes(ledgerId) {
      this.notesLoading = true;

      let payload = {
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId,
        ledgerId: ledgerId
      };

      this.$store.dispatch("getLedgerNotes", payload)
        .then(resp => {
          this.notesLoading = false;
          resp.data ? this.ledgerNotes = resp.data : this.ledgerNotes = [];
          if (!this.ledgerNotes.length) {
            this.$store.dispatch("handleAlert", "No notes found for this unit.");
          }
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.notesLoading = false;
        });
    },

    getStatement(ledgerId) {
      this.statementLoading = true;

      let payload = {
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId,
        ledgerId: ledgerId
      };

      this.$store.dispatch("getLedgerStatement", payload)
        .then(resp => {
          this.statementLoading = false;
          this.ledgerStatement = resp.data;
          if (!this.ledgerStatement.length) {
            this.$store.dispatch("handleAlert", "No statement info found for this unit.");
          }
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.statementLoading = false;
        });
    },

    resetData() {
      this.ledgerNotes = [];
      this.ledgerStatement = [];
      this.paymentHistory = [];
    },

    handleQuickPaySMS() {
      this.quickPayModal = true;
      this.quickPaySMS = true;
      this.quickPayEmail = false;
    },

    handleQuickPayEmail() {
      this.quickPayModal = true;
      this.quickPaySMS = false;
      this.quickPayEmail = true;
    },

    async handleSendQuickPay() {
      this.quickPayLoading = true;
      const quickPayPayload = {
        tenant_id: this.tenant.external_id,
        facility_id: this.$route.params.facilityId,
        email_address: this.quickPayEmail ? this.quickPayEmailAddress : null,
        phone_number: this.quickPaySMS ? this.quickPaySMSNumber : null
      };
      await this.$store.dispatch("sendQuickPay", quickPayPayload)
        .then(() => {
          this.quickPayLoading = false;
          this.closeQuickPayModal();
        })
        .catch(() => {
          this.quickPayLoading = false;
        });
    },

    closeQuickPayModal() {
      this.quickPayModal = false;
      this.quickPaySMS = false;
      this.quickPayEmail = false;
      this.quickPaySMSNumber = this.tenant.phone_number;
      this.quickPayEmailAddress = this.tenant.email;
    }
  },

  computed: {
    ...mapState({
      ledgers: state => state.tenant.ledgers,
      tenant: state => state.tenant.info
    }),

    activeLedger() {
      return this.ledgers[this.selectedLedger];
    },

    sortedLedgers() {
      let sortedLedgers = this.ledgers;
      return sortedLedgers.sort((a, b) => {
        return a.unit_name - b.unit_name;
      });
    },

    invalidQuickPay() {
      if (this.quickPaySMS && this.quickPaySMSNumber && this.quickPaySMSNumber.length === 12) {
        return true;
      };
      if (this.quickPayEmail && this.quickPayEmailAddress && this.validEmailAddress) {
        return true;
      };
      return false;
    },

    validEmailAddress() {
      return /.+@.+/.test(this.quickPayEmailAddress);
    }
  },

  watch: {
    selectedLedger() {
      if (this.activeLedger && (this.activeLedger.facility_unit.name !== this.unitFeatures.number)) {
        this.getUnitDetails();
      }
    }
  },

  mounted() {
    this.quickPaySMSNumber = this.tenant.phone_number;
    this.quickPayEmailAddress = this.tenant.email;
  }
};
</script>
